import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';
import { formatAgo, formatMinor, formatWhen } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import { useBoard } from '@/api/hooks/board';
import { useAnomalies } from '@/api/hooks/anomalies';
import type { BoardRow, BoardUnassignedOrder } from '@/api/types';
import type { StreamStatus } from '@/api/sse';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';
import {
  CardList,
  CardListEmpty,
  CardListItem,
  NarrowOnly,
  WideOnly,
} from '@/components/ui/MobileList';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { VanCard } from '@/components/board/VanCard';
import { AssignDialog } from '@/components/dispatch/AssignDialog';

export function BoardPage() {
  const { snapshot, isLoading, isError, streamStatus, refresh } = useBoard();
  const [assignTarget, setAssignTarget] = useState<BoardUnassignedOrder | null>(null);

  const rows = useMemo(() => snapshot?.vehicles ?? [], [snapshot]);
  const unassigned = useMemo(() => snapshot?.unassigned ?? [], [snapshot]);
  /** Сигналите по бус — същите данни като значката в менюто, без ново зареждане. */
  const anomalies = useAnomalies(false);
  const signals = useMemo(() => {
    const by = new Map<string, { total: number; high: number }>();
    for (const a of anomalies.data?.items ?? []) {
      if (!a.vehicleId) continue;
      const cur = by.get(a.vehicleId) ?? { total: 0, high: 0 };
      cur.total += 1;
      if (a.severity === 'high') cur.high += 1;
      by.set(a.vehicleId, cur);
    }
    return by;
  }, [anomalies.data]);

  useHotkeys([
    { keys: 'r', description: t.common.refresh, group: 'Борд', run: () => refresh() },
    {
      keys: 'a',
      description: `${t.board.assign} (първата заявка без бус)`,
      group: 'Борд',
      run: () => {
        const first = unassigned[0];
        if (first) setAssignTarget(first);
      },
    },
  ]);

  return (
    <div className="flex min-h-full flex-col gap-4 p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0">
          <h1 className="font-display text-lg font-bold tracking-tight">{t.board.title}</h1>
          <p className="text-xs text-ink-dim">
            {t.board.subtitle} · {t.board.lastUpdate} {formatAgo(snapshot?.at)}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <ConnectionState status={streamStatus} />
          <Button
            size="sm"
            variant="secondary"
            icon={<RefreshCw className="size-3.5" />}
            onClick={refresh}
          >
            {t.common.refresh}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <LoadingBlock />
      ) : isError ? (
        <ErrorState onRetry={refresh} />
      ) : rows.length === 0 ? (
        <Card>
          <CardListEmpty>{t.board.noVehicles}</CardListEmpty>
        </Card>
      ) : (
        <>
          <Summary rows={rows} waiting={unassigned.length} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {rows.map((row) => (
              <VanCard key={row.vehicleId} row={row} signals={signals.get(row.vehicleId)} />
            ))}
          </div>
        </>
      )}

      <Card>
        <CardHeader
          title={t.board.unassignedOrders}
          subtitle={
            unassigned.length === 0
              ? 'Всичко потвърдено е разпределено.'
              : `${unassigned.length} потвърдени заявки чакат бус`
          }
        />
        {/* Тесен екран: заявката без бус е карта с едно едро действие „Назначи". */}
        <NarrowOnly>
          {unassigned.length === 0 ? (
            <CardListEmpty>Няма заявки без бус.</CardListEmpty>
          ) : (
            <CardList>
              {unassigned.map((order) => (
                <CardListItem key={order.id}>
                  <div className="flex items-baseline gap-2">
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: order.id }}
                      data-tap
                      className="font-mono text-sm text-accent underline underline-offset-4"
                    >
                      {order.number}
                    </Link>
                    {order.customerRef ? (
                      <span className="truncate text-[11px] text-ink-faint">
                        реф. {order.customerRef}
                      </span>
                    ) : null}
                    <span className="ml-auto shrink-0 font-mono text-sm tabular">
                      {formatMinor(order.agreedPriceMinor, order.ccy)}
                    </span>
                  </div>
                  <div className="mt-1 truncate text-sm text-ink-dim">{order.partnerName}</div>
                  <div className="mt-0.5 text-sm">
                    {order.fromCity} ({order.fromCountry}) → {order.toCity} ({order.toCountry})
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-dim">
                    <span>
                      {t.quote.readyAt}: {formatWhen(order.readyAt)}
                    </span>
                    <span className={order.isHardDeadline ? 'text-warn' : undefined}>
                      {t.orders.deadline}: {formatWhen(order.deadlineAt)}
                    </span>
                    {order.isHardDeadline ? <Badge tone="warn">твърд</Badge> : null}
                  </div>
                  <Button
                    className="mt-2 w-full"
                    variant="primary"
                    onClick={() => setAssignTarget(order)}
                  >
                    {t.board.assign}
                  </Button>
                </CardListItem>
              ))}
            </CardList>
          )}
        </NarrowOnly>

        <WideOnly>
          <Table>
            <THead>
              <TR>
                <TH>{t.orders.number}</TH>
                <TH>{t.orders.partner}</TH>
                <TH>{t.orders.route}</TH>
                <TH>{t.quote.readyAt}</TH>
                <TH>{t.orders.deadline}</TH>
                <TH numeric>{t.orders.price}</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {unassigned.length === 0 ? (
                <TableEmpty colSpan={7}>Няма заявки без бус.</TableEmpty>
              ) : (
                unassigned.map((order) => (
                  <TR key={order.id}>
                    <TD>
                      <Link
                        to="/orders/$orderId"
                        params={{ orderId: order.id }}
                        className="font-mono text-sm text-accent hover:underline"
                      >
                        {order.number}
                      </Link>
                      {order.customerRef ? (
                        <div className="text-[11px] text-ink-faint">реф. {order.customerRef}</div>
                      ) : null}
                    </TD>
                    <TD className="max-w-[16rem] truncate">{order.partnerName}</TD>
                    <TD className="whitespace-nowrap text-ink-dim">
                      {order.fromCity} ({order.fromCountry}) → {order.toCity} ({order.toCountry})
                    </TD>
                    <TD className="whitespace-nowrap">{formatWhen(order.readyAt)}</TD>
                    <TD className="whitespace-nowrap">
                      <span className={order.isHardDeadline ? 'text-warn' : undefined}>
                        {formatWhen(order.deadlineAt)}
                      </span>
                      {order.isHardDeadline ? (
                        <Badge tone="warn" className="ml-1">
                          твърд
                        </Badge>
                      ) : null}
                    </TD>
                    <TD numeric>{formatMinor(order.agreedPriceMinor, order.ccy)}</TD>
                    <TD className="text-right">
                      <Button size="sm" variant="primary" onClick={() => setAssignTarget(order)}>
                        {t.board.assign}
                      </Button>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </WideOnly>
      </Card>

      {assignTarget ? (
        <AssignDialog
          open
          orderId={assignTarget.id}
          orderNumber={assignTarget.number}
          onClose={() => setAssignTarget(null)}
        />
      ) : null}
    </div>
  );
}

/** Едно изречение с числа — отговорът на „какво става" преди да се гледат картите. */
function Summary({ rows, waiting }: { rows: BoardRow[]; waiting: number }) {
  const onTrip = rows.filter((r) => r.currentOrder !== null).length;
  const moving = rows.filter((r) => r.position?.motionState === 'moving').length;
  const free = rows.filter((r) => r.currentOrder === null && r.status === 'active').length;
  const offline = rows.filter((r) => !r.position || r.position.motionState === 'offline').length;
  const items: { label: string; value: number; tone: string }[] = [
    { label: 'на курс', value: onTrip, tone: 'text-accent' },
    { label: 'карат в момента', value: moving, tone: 'text-ink' },
    { label: 'свободни', value: free, tone: 'text-ink' },
    { label: 'заявки чакат бус', value: waiting, tone: waiting > 0 ? 'text-warn' : 'text-ink' },
    { label: 'без връзка', value: offline, tone: offline > 0 ? 'text-danger' : 'text-ink' },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
      {items.map((i) => (
        <div key={i.label} className="rounded-xl border border-line bg-surface px-4 py-3">
          <div className={cn('font-display text-2xl font-bold tabular', i.tone)}>{i.value}</div>
          <div className="text-xs text-ink-dim">{i.label}</div>
        </div>
      ))}
    </div>
  );
}

function ConnectionState({ status }: { status: StreamStatus }) {
  const map: Record<StreamStatus, { label: string; tone: 'ok' | 'warn' | 'danger' | 'info' }> = {
    live: { label: t.board.live, tone: 'ok' },
    connecting: { label: t.common.loading, tone: 'info' },
    reconnecting: { label: t.board.reconnecting, tone: 'warn' },
    polling: { label: t.board.polling, tone: 'warn' },
  };
  const state = map[status];
  return (
    <Badge tone={state.tone}>
      <span
        className={cn(
          'size-1.5 rounded-full',
          status === 'live'
            ? 'animate-pulse bg-ok'
            : status === 'polling'
              ? 'bg-warn'
              : 'bg-current',
        )}
      />
      {state.label}
    </Badge>
  );
}

/**
 * Сървърът връща каботажни броячи за всяка държава, в която бусът има отворен
 * прозорец. На борда има място за един чип, затова показваме най-стегнатия —
 * този с най-малко оставащи операции. Пълният списък е в детайла на буса.
 */

/**
 * Алармите пристигат като готов български текст. Нивото се извежда от
 * съдържанието: липсващият или стар тахограф спира буса, останалото е
 * предупреждение.
 */
